import { useState, useRef } from "react";
import { useForm, Controller } from "react-hook-form";

// Mock EditButton component since it's imported but not provided
const EditAnswerButton = ({ onPress, disabled }) => (
  <button
    onClick={onPress}
    disabled={disabled}
    style={{
      background: "none",
      border: "none",
      cursor: disabled ? "default" : "pointer",
      padding: "4px",
    }}
  >
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path
        d="M14.166 2.5c.442 0 .866.176 1.179.488l1.667 1.667a1.667 1.667 0 0 1 0 2.357l-10 10A1.667 1.667 0 0 1 5.833 17.5H4.166c-.92 0-1.666-.746-1.666-1.667v-1.666c0-.442.175-.866.488-1.179l10-10c.313-.312.737-.488 1.178-.488Z"
        stroke="#8798A7"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </button>
);

export const MultipleAnswerInput = ({
  questionId,
  label,
  defaultValue = [],
  onValueChange,
}) => {
  // Initialize with defaultValue or one empty field
  const getInitialFields = () => {
    if (Array.isArray(defaultValue) && defaultValue.length > 0) {
      return defaultValue.map((val) => String(val));
    }
    return [""];
  };

  const [answerFields, setAnswerFields] = useState(getInitialFields);
  const [focusedIndex, setFocusedIndex] = useState(null);

  const handleAddField = () => {
    const newFields = [...answerFields, ""];
    setAnswerFields(newFields);
    notifyChange(newFields);
  };

  const handleRemoveField = (index) => {
    if (answerFields.length > 1) {
      const newFields = answerFields.filter((_, i) => i !== index);
      setAnswerFields(newFields);
      notifyChange(newFields);
    }
  };

  const handleFieldChange = (index, value) => {
    const newFields = [...answerFields];
    newFields[index] = value;
    setAnswerFields(newFields);
    notifyChange(newFields);
  };

  const notifyChange = (fields) => {
    if (onValueChange) {
      // Filter out empty values and return as array
      const filteredAnswers = fields.filter((ans) => ans && ans.trim() !== "");
      onValueChange(filteredAnswers);
    }
  };

  const { control } = useForm();

  return (
    <div className="d-flex flex-column w-full">
      {label && (
        <label
          style={{
            color: "#C8CED3",
            fontSize: 16,
            fontWeight: 500,
            marginBottom: "24px",
          }}
        >
          {label}
        </label>
      )}

      <div className="d-flex flex-column" style={{ gap: "16px" }}>
        {answerFields.map((answer, index) => (
          <AnswerFieldItem
            key={`${questionId}-${index}`}
            control={control}
            name={`${questionId}-${index}`}
            value={answer}
            index={index}
            isFocused={focusedIndex === index}
            onFocus={() => setFocusedIndex(index)}
            onBlur={() => setFocusedIndex(null)}
            onChange={(value) => handleFieldChange(index, value)}
            onRemove={() => handleRemoveField(index)}
            canRemove={answerFields.length > 1}
          />
        ))}
      </div>

      <div
        className="d-flex justify-content-center"
        style={{ marginTop: "24px" }}
      >
        <button
          className="btn-1 btn-1-primary"
          onClick={handleAddField}
          type="button"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "12px 24px",
            backgroundColor: "#0D6EFD",
            color: "white",
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
          }}
        >
          <span style={{ fontSize: "20px", lineHeight: "1" }}>+</span>
          Add
        </button>
      </div>
    </div>
  );
};

const AnswerFieldItem = ({
  control,
  name,
  value,
  index,
  isFocused,
  onFocus,
  onBlur,
  onChange,
  onRemove,
  canRemove,
}) => {
  const inputRef = useRef(null);

  const resizeTextarea = (textarea) => {
    if (textarea) {
      textarea.style.height = "auto";
      const scrollHeight = textarea.scrollHeight;
      const maxHeight = 120;
      textarea.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
    }
  };

  const handleEditClick = () => {
    if (inputRef.current) {
      inputRef.current.focus();
      onFocus();
    }
  };

  return (
    <div className="d-flex flex-column w-full">
      <div className="d-flex flex-column relative w-full rounded-[24px]">
        <div className="col-md-12">
          <div
            className="d-flex"
            style={{ gap: "12px", alignItems: "flex-start" }}
          >
            <div style={{ flex: 1, position: "relative" }}>
              <div className="textfield-1" style={{ position: "relative" }}>
                <Controller
                  name={name}
                  control={control}
                  defaultValue={value}
                  render={({ field }) => {
                    return (
                      <>
                        <textarea
                          ref={(el) => {
                            inputRef.current = el;
                            if (el) {
                              setTimeout(() => resizeTextarea(el), 0);
                            }
                          }}
                          className={`text-body-b2 no-scrollbar ${
                            !isFocused ? "answer-input-no-border" : ""
                          }`}
                          placeholder={`Action ${index + 1}`}
                          value={field.value ?? ""}
                          onChange={(e) => {
                            field.onChange(e.target.value);
                            onChange(e.target.value);
                            resizeTextarea(e.target);
                          }}
                          onFocus={() => {
                            field.onBlur();
                            onFocus();
                          }}
                          onBlur={() => {
                            field.onBlur();
                            onBlur();
                          }}
                          rows={1}
                          style={{
                            paddingRight: "50px",
                            transition: "border 0.2s ease",
                            backgroundColor: "#1B2C3A",
                            borderRadius: "24px",
                            padding: "12px 16px",
                            fontSize: "16px",
                            fontWeight: "400",
                            lineHeight: "24px",
                            color: "#C8CED3",
                            minHeight: "48px",
                            maxHeight: "120px",
                            resize: "none",
                            overflowY: "auto",
                            overflowX: "hidden",
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "pre-wrap",
                            border: isFocused
                              ? "1px solid #c8ced3"
                              : "1px solid transparent",
                            width: "100%",
                          }}
                        />
                        <div
                          style={{
                            position: "absolute",
                            right: "16px",
                            top: "12px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <EditAnswerButton
                            onPress={handleEditClick}
                            disabled={false}
                          />
                        </div>
                      </>
                    );
                  }}
                />
              </div>
            </div>
            {canRemove && (
              <button
                onClick={onRemove}
                type="button"
                style={{
                  width: "65px",
                  height: "56px",
                  borderRadius: "20px",
                  backgroundColor: "#6C757D",
                  border: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                <span
                  style={{
                    color: "#fff",
                    fontSize: "24px",
                    fontWeight: "bold",
                    lineHeight: "1",
                  }}
                >
                  −
                </span>
              </button>
            )}
          </div>
        </div>
      </div>

      <div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-end",
            alignItems: "flex-end",
            color: "#8798A7",
            marginTop: "12px",
          }}
        >
          Character{(value?.length ?? 0) > 1 ? "s" : ""}: {value?.length ?? 0}
        </div>
      </div>
    </div>
  );
};
